import React, { useState } from 'react';
import {
  Table, Card, Button, Space, Typography, Form, Input, Select,
  Tag, Drawer, InputNumber, message, Modal, Empty, Tooltip
} from 'antd';
import { PlusOutlined, SearchOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

// ══════════════════════════════════════════════
//  Mock 数据
// ══════════════════════════════════════════════
const MOCK_SUB_ENTERPRISES = [
  { value: 'test-sub-001', label: '智联华东分销 (test-sub-001)' },
  { value: 'test-sub-002', label: '智联华南分销 (test-sub-002)' },
  { value: 'test-sub-003', label: '云视北方代理 (test-sub-003)' },
];

// 数仓返回的全球国家列表
const MOCK_COUNTRIES = [
  '中国', '美国', '日本', '韩国', '德国', '英国', '法国', '澳大利亚', '加拿大', '新加坡',
];

// TODO: 联调时替换为接口数据
const MOCK_REDIST_RULES = [
  { key: '1', subEntId: 'test-sub-001', subEntName: '智联华东分销', baseRatio: 50, customRatios: [{ country: '美国', ratio: 40 }], status: 'active', updateTime: '2026-07-28 15:00:00' },
  { key: '2', subEntId: 'test-sub-002', subEntName: '智联华南分销', baseRatio: 30, customRatios: [], status: 'active', updateTime: '2026-07-27 11:30:00' },
  { key: '3', subEntId: 'test-sub-003', subEntName: '云视北方代理', baseRatio: 0, customRatios: [{ country: '德国', ratio: 35 }, { country: '英国', ratio: 45 }], status: 'stopped', updateTime: '2026-07-25 10:00:00' },
];

// 比例展示：统一比例 + 自定义国家 Tag
const renderRatio = (record) => (
  <Space size={4} wrap>
    <Text strong style={{ color: '#1677ff' }}>{record.baseRatio}%</Text>
    {record.customRatios && record.customRatios.length > 0 && (
      record.customRatios.length <= 2
        ? record.customRatios.map((c, i) => (
            <Tooltip key={i} title={`${c.country} 自定义比例`}>
              <Tag color="orange" style={{ border: 'none' }}>{c.country} {c.ratio}%</Tag>
            </Tooltip>
          ))
        : <Tooltip title={record.customRatios.map((c) => `${c.country} ${c.ratio}%`).join('；')}>
            <Tag color="orange" style={{ border: 'none' }}>自定义{record.customRatios.length}国</Tag>
          </Tooltip>
    )}
  </Space>
);

const Again = () => {
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState(MOCK_REDIST_RULES);
  const [filterEnt, setFilterEnt] = useState('');
  const [filterStatus, setFilterStatus] = useState(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  // Mock: 假设当前为设备企业，已有下级企业
  const hasSubEnts = MOCK_SUB_ENTERPRISES.length > 0;

  const filteredData = dataSource.filter((r) => {
    if (filterEnt && !(r.subEntName + r.subEntId).toLowerCase().includes(filterEnt.toLowerCase())) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    return true;
  });

  const openDrawer = (record) => {
    if (record) {
      setEditingRecord(record);
      form.setFieldsValue({
        subEntId: record.subEntId,
        baseRatio: record.baseRatio,
        customRatios: record.customRatios && record.customRatios.length > 0 ? record.customRatios : [],
      });
    } else {
      setEditingRecord(null);
      form.resetFields();
      form.setFieldsValue({ baseRatio: 0, customRatios: [] });
    }
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const ent = MOCK_SUB_ENTERPRISES.find((e) => e.value === values.subEntId);
      const customRatios = (values.customRatios || []).filter((c) => c.country && c.ratio != null);

      if (editingRecord) {
        setDataSource((prev) =>
          prev.map((r) =>
            r.key === editingRecord.key
              ? { ...r, baseRatio: values.baseRatio, customRatios, updateTime: new Date().toISOString().replace('T', ' ').slice(0, 19) }
              : r,
          ),
        );
        message.success('再分配规则编辑成功，下一结算周期生效');
      } else {
        const newRecord = {
          key: Date.now().toString(),
          subEntId: values.subEntId,
          subEntName: ent?.label.split(' ')[0] || '',
          baseRatio: values.baseRatio,
          customRatios,
          status: 'active',
          updateTime: new Date().toISOString().replace('T', ' ').slice(0, 19),
        };
        setDataSource((prev) => [newRecord, ...prev]);
        message.success('再分配规则创建成功，下一结算周期生效');
      }
      setDrawerOpen(false);
    } catch {
      // validation failed
    }
  };

  const toggleStatus = (record) => {
    const isStopping = record.status === 'active';
    Modal.confirm({
      title: isStopping ? '停用再分配规则' : '启用再分配规则',
      content: isStopping
        ? '停用后不再参与后续再分配计算，已生成的账单不受影响。确认停用？'
        : '启用后将在下一结算周期参与再分配计算。确认启用？',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        setDataSource((prev) =>
          prev.map((r) =>
            r.key === record.key ? { ...r, status: isStopping ? 'stopped' : 'active', updateTime: new Date().toISOString().replace('T', ' ').slice(0, 19) } : r,
          ),
        );
        message.success(isStopping ? '规则已停用' : '规则已启用');
      },
    });
  };

  const deleteRule = (record) => {
    Modal.confirm({
      title: '删除再分配规则',
      content: '删除后不可恢复，确认删除？',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        setDataSource((prev) => prev.filter((r) => r.key !== record.key));
        message.success('规则已删除');
      },
    });
  };

  const columns = [
    { title: '下级企业', dataIndex: 'subEntName', width: 160, render: (text, r) => <div><Text strong>{text}</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{r.subEntId}</Text></div> },
    { title: '再分配比例', key: 'ratio', width: 180, render: (_, r) => renderRatio(r) },
    { title: '生效状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'} style={{ border: 'none' }}>{v === 'active' ? '生效中' : '已停用'}</Tag> },
    { title: '操作时间', dataIndex: 'updateTime', width: 160, render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> },
    {
      title: '操作', key: 'action', width: 200, fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openDrawer(r)}>编辑</Button>
          <Button type="link" size="small" onClick={() => toggleStatus(r)}>
            {r.status === 'active' ? '停用' : '启用'}
          </Button>
          <Button type="link" size="small" danger onClick={() => deleteRule(r)}>删除</Button>
        </Space>
      ),
    },
  ];

  if (!hasSubEnts) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>分润再分配</Title>
        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginTop: 16 }}>
          <Empty description="您当前没有下级企业，无法创建再分配规则">
            <Button type="primary" onClick={() => navigate('/company/list')}>去管理下级企业</Button>
          </Empty>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>分润再分配</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer(null)}>创建再分配规则</Button>
      </div>

      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form layout="inline">
          <Form.Item label="下级企业" style={{ marginBottom: 0 }}>
            <Input placeholder="搜索企业名称/ID" prefix={<SearchOutlined />} style={{ width: 220 }} value={filterEnt} onChange={(e) => setFilterEnt(e.target.value)} allowClear />
          </Form.Item>
          <Form.Item label="状态" style={{ marginBottom: 0 }}>
            <Select placeholder="全部" allowClear style={{ width: 120 }} value={filterStatus} onChange={setFilterStatus}>
              <Option value="active">生效中</Option>
              <Option value="stopped">已停用</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button icon={<ReloadOutlined />} onClick={() => { setFilterEnt(''); setFilterStatus(undefined); }}>重置</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="key"
          bordered={false}
          scroll={{ x: 1100 }}
          locale={{ emptyText: '暂无再分配规则，点击右上角创建' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
        />
      </Card>

      <Drawer
        title={editingRecord ? '编辑再分配规则' : '创建再分配规则'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button style={{ marginRight: 8 }} onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" onClick={handleSubmit}>保存</Button>
          </div>
        }
      >
        <div style={{ background: '#e6f4ff', padding: '12px 16px', borderRadius: 8, marginBottom: 24, border: '1px solid #91caff' }}>
          <Text style={{ fontSize: 13 }}>你可对设备分销商创建广告分润。</Text>
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              分润规则：分销到下级企业的活跃设备数 / 分销到下级企业的所有设备数 × 你获得的广告分润收益 × 你为下级设置的再分配比例。
            </Text>
          </div>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item name="subEntId" label="下级分销企业" rules={[{ required: true, message: '请选择下级分销企业' }]}>
            <Select placeholder="选择下级分销企业" showSearch optionFilterProp="children" disabled={!!editingRecord}>
              {MOCK_SUB_ENTERPRISES.map((e) => <Option key={e.value} value={e.value}>{e.label}</Option>)}
            </Select>
          </Form.Item>
          <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            <Form.Item
              name="baseRatio"
              label={
                <Space size={4}>
                  统一再分配比例
                  <Tooltip title="统一配置，对所有国家生效；可设为 0%">
                    <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>ⓘ</Text>
                  </Tooltip>
                </Space>
              }
              rules={[
                { required: true, message: '请输入统一再分配比例' },
                { type: 'number', min: 0, max: 100, message: '再分配比例需在 0%〜100% 之间' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} placeholder="对所有国家生效，可设为 0%" min={0} max={100} precision={0} addonAfter="%" />
            </Form.Item>

            <div style={{ marginBottom: 8 }}>
              <Text strong>自定义国家比例</Text>
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>自定义优先于统一比例，可选</Text>
            </div>
            <Form.List name="customRatios">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                      <Form.Item {...restField} name={[name, 'country']} style={{ marginBottom: 0 }} rules={[{ required: true, message: '请选择国家' }]}>
                        <Select placeholder="国家" allowClear showSearch style={{ width: 160 }}>
                          {MOCK_COUNTRIES.map((c) => <Option key={c} value={c}>{c}</Option>)}
                        </Select>
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'ratio']} style={{ marginBottom: 0 }} rules={[
                        { required: true, message: '请输入比例' },
                        { type: 'number', min: 0, max: 100, message: '0~100' },
                      ]}>
                        <InputNumber placeholder="比例%" min={0} max={100} precision={0} addonAfter="%" style={{ width: 120 }} />
                      </Form.Item>
                      <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} danger />
                    </Space>
                  ))}
                  <Button type="dashed" block onClick={() => add()} style={{ marginTop: 4 }}>+ 添加自定义国家比例</Button>
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default Again;
